/*
  Eva Radio: floating draggable tape-deck widget. Vanilla JS, no
  dependencies. Persists open/closed, position, and playback state in
  localStorage under "evaRadioState" so it survives hard reloads and
  PJAX navigation, since this whole widget lives outside the swapped
  #MainContent region.
*/
(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  // Same ?tapedev=1 convention eva-radio-tape.js already uses for its own
  // dev-only logging -- gates the FF/REW sound-trigger console.debug lines
  // below so verify can prove both buttons actually fire the same sound
  // without leaving noise in the console for normal visitors.
  function isTapeDevMode() {
    try { return new URLSearchParams(window.location.search).get('tapedev') === '1'; } catch (e) { return false; }
  }

  var STATE_KEY = 'evaRadioState';
  var MARGIN = 24; // default spawn distance from the document edge
  // Room reserved on top of MARGIN so the tape rack (eva-radio-tape.js,
  // anchored left:100% + margin-left:16px off the widget's own right
  // edge) doesn't overflow the document at the widget's DEFAULT spawn
  // position. Found 2026-08-05: defaultPosition() below only ever
  // accounted for the widget's own 340px box, not the rack that sits
  // beside it -- confirmed live via Playwright at 1440/1600/1920px that
  // the rack overflowed the document edge by ~113px at every one of
  // them (rack footprint ~137px vs MARGIN's 24px, and the gap didn't
  // shrink on a wider viewport since the widget's own default position
  // is ALSO computed off viewportWidth() (was documentWidth() before
  // the 2026-08-09 fixed-position rework), not a fixed offset -- it
  // keeps the same ~24px right margin regardless of window width). A
  // first-time visitor got an unreachable rack until they happened to
  // drag the whole widget left first, with nothing prompting that.
  // 137px measured; a little headroom rather than the bare minimum.
  var RACK_RESERVE = 150;
  var DRAG_THRESHOLD = 4; // px of movement before a pointerdown counts as a drag, not a click
  var MIN_WIDTH = 200; // desktop/mouse corner-handle floor only -- touch's own floor is naturalWidth(), see below
  // 680 = ~2x the 340px CSS default (2026-08-25, Per's ask: "resizable up
  // to roughly double its default size on desktop"). Was 560 (~1.65x) --
  // raising this alone would have made the widget overlap the header
  // nav/LCD clock/marquee at a large size; safeTop()/defaultPosition()
  // below still keep the widget's fresh-spawn position clear of that
  // strip, but (2026-08-26) clampPosition() no longer enforces safeTop()
  // as a DRAG floor -- Per: the widget may now be dragged anywhere,
  // including over the header/nav, the minimize control is the intended
  // way to get it out of the way. Desktop-only; MAX_WIDTH itself doesn't
  // apply to touch, see IS_TOUCH_CAPABLE/naturalWidth() below.
  var MAX_WIDTH = 680;
  // Feature-detected, not screen-width-derived -- a screen-width check
  // would misjudge a touchscreen laptop (often a desktop-width viewport)
  // and would flip on/off with nothing but a DevTools viewport resize
  // even when no touch input is involved. maxTouchPoints/ontouchstart
  // report actual input capability regardless of viewport size. (Same
  // detection b520551 used before its 2026-08-26 00:01 revert; pinch
  // itself is a full rebuild below, not a restore of that code.)
  var IS_TOUCH_CAPABLE = ('ontouchstart' in window) ||
    (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0);
  // How much of the control bar must stay on screen once it is too big
  // to fit entirely -- only reachable at the upper end of the 350% pinch
  // range. 44px is the standard minimum touch target, i.e. "enough to
  // actually put a thumb on", not merely enough to see.
  var CONTROL_BAR_MIN_VISIBLE_PX = 44;
  var SEEK_SECONDS = 10; // tap step
  var SAVE_INTERVAL_MS = 4000; // throttle position persistence while playing
  var PLAY_SOUND_LEAD_MS = 150; // PLAY hotspot: clack starts, music enters this far into it
  var SEEK_HOLD_THRESHOLD_MS = 180; // press duration before a tap becomes a hold
  var FFW_PLAYBACK_RATE = 4; // within spec's 3-6 range
  var FFW_DUCK_VOLUME = 0.4;
  var REW_DUCK_VOLUME = FFW_DUCK_VOLUME; // Per: REW should sound the same as FFW while held, not near-silent -- was 0.10
  var KEYBOARD_DUCK_VOLUME = 0.65; // keys+tape play at once now -- lower "just a tad" per Per, not the near-silent FFW/REW duck
  var REW_SCRUB_STEP_SECONDS = 2;
  var REW_SCRUB_INTERVAL_MS = 250;

  // Tapes: zero tape data lives in this file. The manifest (assets/eva-
  // tapes.json, schema v1) is fetched once at init -- see loadTapesManifest()
  // below -- and populates this array. Each side is an ARRAY OF TRACK
  // OBJECTS ({title, url, gainDb}) in play order, not a lookup into a
  // separate number-keyed map (that indirection was a v1 artifact of how
  // Per originally supplied the files; the JSON schema flattens it away).
  var tapes = [];

  var DEFAULTS = {
    minimized: true, // was "open: false" -- inverted sense, renamed to match the new minimize/restore terminology
    x: null,
    y: null,
    width: null, // resized width in px; null = use the CSS default (responsive, see eva-radio.css)
    userMoved: false,
    tapeId: null,
    sideId: null,
    trackIndex: 0,
    positionSec: 0,
    playing: false,
    angle: 'front' // 'front' | 'above' -- decides which hotspot layer (tape/keyboard) is visible; keys + tape both work regardless of which angle is showing, no separate mode
  };

  function loadState() {
    try {
      var raw = localStorage.getItem(STATE_KEY);
      if (!raw) { return Object.assign({}, DEFAULTS); }
      var parsed = JSON.parse(raw);
      return Object.assign({}, DEFAULTS, parsed);
    } catch (e) {
      return Object.assign({}, DEFAULTS);
    }
  }

  function saveState() {
    try {
      localStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch (e) {}
  }

  var state = loadState();

  // Resolves eva-tapes.json's image slots (2026-08-03) -- the manifest
  // stores bare filenames now, not full URLs (a hardcoded URL baked in a
  // fixed Shopify theme-asset-slot number, e.g. ".../t/14/assets/...",
  // silently went stale the moment this draft theme's own slot turned
  // out to be t/10 instead -- see the window.EvaTapesImageAssets comment
  // in theme.liquid for the full incident). window.EvaTapesImageAssets
  // (theme.liquid, asset_url-resolved fresh on every page load) is the
  // only source of truth for the real URL. A filename with no matching
  // entry is left as-is rather than dropped -- shouldn't happen since
  // theme.liquid's map is meant to list every filename any tape uses,
  // but failing open here (falling through to renderFace()'s existing
  // silent-404 handling) is safer than a tape rendering with a face
  // missing entirely over one unlisted filename.
  function resolveTapeImages(list) {
    var map = window.EvaTapesImageAssets || {};
    list.forEach(function (tape) {
      if (!tape.images) { return; }
      Object.keys(tape.images).forEach(function (key) {
        var filename = tape.images[key];
        if (filename && map[filename]) { tape.images[key] = map[filename]; }
      });
    });
    return list;
  }

  // RACK ORDER IS RANDOMISED PER VISIT -- 2026-09-24, implementing Per's
  // 2026-09-02 ruling, which had never reached the code. This line used
  // to sort by dateAdded ASC ("oldest/first-added tape first, per Per's
  // rack ordering spec"), and that comment was the spec it superseded.
  // Checked before changing it: `git log -S"shuffle" -- assets/eva-radio
  // .js` is empty, so no shuffle was ever here to regress. It never
  // shipped rather than broke.
  //
  // ONE SHUFFLE PER PAGE LOAD IS ALL THE STABILITY NEEDED, with nothing
  // stored. This file's <script src> lives in layout/theme.liquid,
  // outside anything assets/eva-pjax.js swaps, and that file re-executes
  // only scripts explicitly marked data-eva-pjax-exec -- so eva-radio.js
  // runs once per real page load and its module scope, `tapes` included,
  // survives every PJAX navigation in the visit. The arrows, the
  // carousel and insert all read that same array, so none of them can
  // reshuffle mid-session. A sessionStorage copy of the order would have
  // been a second source of truth for no gain.
  //
  // NOTHING DEPENDED ON THE OLD ORDER, checked rather than assumed: the
  // two places in assets/eva-radio-tape.js that mention dateAdded (the
  // focusedTapeIndex note and the keyboard-nav note) are comments only,
  // both corrected in this pass, and no code picks a tape by index --
  // there is no tapes[0] default anywhere.
  //
  // Fisher-Yates, so every permutation is equally likely and no tape is
  // favoured for any slot. Deliberately NOT shared with the identically
  // named helper in assets/eva-radio-station.js: these are separate
  // plain-.js assets with separate IIFE scopes and no module system
  // between them, so sharing would mean a global.
  function shuffleInPlace(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var swap = arr[i];
      arr[i] = arr[j];
      arr[j] = swap;
    }
    return arr;
  }

  // Fetched once here (window.EvaRadioTapesManifestUrl comes from
  // theme.liquid, since this plain .js asset can't call asset_url itself)
  // and reused for the life of the page -- the browser's own HTTP cache
  // (the URL is fingerprinted, ?v=...) makes repeat PJAX navigations free,
  // no extra caching layer needed. Array position in the JSON itself
  // carries no meaning, and after the shuffle neither does dateAdded --
  // it stays in the manifest because the tape cards still show it.
  var tapesReady = fetch(window.EvaRadioTapesManifestUrl || '', { credentials: 'omit' })
    .then(function (res) {
      if (!res.ok) { throw new Error('HTTP ' + res.status); }
      return res.json();
    })
    .then(function (data) {
      var list = (data && data.tapes) || [];
      shuffleInPlace(list);
      return resolveTapeImages(list);
    })
    .catch(function (err) {
      console.error('[eva-radio] failed to load tape manifest', err);
      return [];
    })
    .then(function (list) {
      tapes = list;
      document.dispatchEvent(new CustomEvent('eva:radio:tapes:ready', { detail: { tapes: tapes } }));
      return tapes;
    });

  var widget = null;
  var dragHandle = null;
  var stage = null; // locked-aspect box every deck layer (img, overlay, hubs, hotspots) positions against -- see eva-radio.css's .eva-radio-widget__stage
  var img = null;
  var bubble = null;
  var bubbleText = null;
  var bubbleAction = null;
  var bubbleClose = null;
  var audio = null;
  var toggleBtns = [];
  var minimizeBtn = null;
  var resizeHandle = null;
  // Mobile-overflow fix (2026-08-09) -- these two, plus resizeHandle
  // above, are the "satellite" elements that stick out beyond the
  // widget's own frame box (radioToggleEl: the vertical RADIO-mode tab
  // on the right edge; tapeRackEl: the tape rack, which sits beside the
  // widget on wide viewports and below it on narrow ones). clampPosition()
  // below measures their live overflow rather than a hardcoded px/percent
  // guess, so the clamp bounds can't drift out of sync with the art the
  // way a hardcoded reserve value already once did (see RACK_RESERVE's
  // own history above).
  var radioToggleEl = null;
  var tapeRackEl = null;
  var onAirDots = [];
  var angleBtn = null;
  var angleImageCache = {}; // preloaded Image() objects, keyed by angle -- avoids a flash of blank/broken img on first cycle to each angle
  var keyboardHotspotsSvg = null;
  // Transport hotspots (door/play/rew/ff/stop/pause), one SVG per angle,
  // queried into this map in ready(). Each is calibrated to its own photo
  // (see eva-radio.liquid), so applyAngle() just shows the one matching
  // state.angle and hides the rest -- the actions/sound/press-animation
  // code is entirely angle-agnostic already (keyed by data-eva-radio-
  // action + the .eva-radio-hotspot class, not by which SVG the element
  // lives in).
  var angleTransportHotspots = {};

  // --- Angle changer ---------------------------------------------------
  // 'front' shows the tape transport hotspots, 'above' the keyboard --
  // left/right are retired (Per: reduce to two angles), photos kept in
  // _source/radio/ but no longer shipped/cycled. Switching angle never
  // touches playback -- audio keeps going if it was already playing,
  // only the hotspot layer and speaker-viz canvas hide/show.

  var ANGLES = ['front', 'above']; // left/right retired (Per: reduce to two angles) -- photos kept in _source/radio/, no longer shipped

  function preloadAngleImages() {
    var assets = window.EvaRadioAngleAssets || {};
    ANGLES.forEach(function (a) {
      if (!assets[a] || angleImageCache[a]) { return; }
      var im = new Image();
      im.src = assets[a];
      angleImageCache[a] = im;
    });
  }

  // Keys + tape both work at once now -- there's no separate "mode" to
  // switch between (removed per Per). The angle-changer is the only thing
  // that decides which hotspot layer is visible/interactive. Every angle
  // (front/above/left/right) has its own transport hotspot SVG (door +
  // play/rew/ff/stop/pause), calibrated to that angle's own photo -- see
  // angleTransportHotspots. 'above' additionally shows the keyboard
  // layer. Desktop keyboard shortcuts (see eva-radio-keyboard.js) are NOT
  // gated by angle at all -- they work regardless of which photo is
  // currently showing, since they don't need the on-image hotspots to be
  // visible.
  // Keeps .eva-radio-widget__stage's aspect-ratio (and therefore every
  // overlay/hub percentage that resolves against it) matched to whichever
  // angle photo is actually loaded -- front and above are different
  // intrinsic ratios (1901x1057 vs. 2117x955), so this has to be re-read
  // from the img every time applyAngle() runs, not set once. Safe to call
  // unconditionally (idempotent) even when the src didn't change.
  function syncStageAspect() {
    if (!img || !stage) { return; }
    function setFromImg() {
      if (img.naturalWidth && img.naturalHeight) {
        stage.style.setProperty('--eva-radio-stage-aspect', img.naturalWidth + ' / ' + img.naturalHeight);
      }
    }
    if (img.complete) {
      setFromImg();
    } else {
      img.addEventListener('load', setFromImg, { once: true });
    }
  }

  function applyAngle() {
    // A localStorage value from before left/right were retired would
    // otherwise match no angle at all -- neither hotspot layer's display
    // check would pass, leaving the widget uninteractive until the user
    // happened to click the angle button once to cycle away from it.
    if (ANGLES.indexOf(state.angle) === -1) { state.angle = 'front'; }
    var assets = window.EvaRadioAngleAssets || {};
    var url = assets[state.angle];
    if (url && img && img.src !== url) { img.src = url; }
    syncStageAspect();
    // Both remaining angles have their own calibrated door hotspot (see
    // eva-radio.liquid) -- this used to be hardcoded to the front angle
    // only, which silently blocked drag/tap-insert on 'above' even after
    // its door polygon existed. True for any angle with hotspots at all.
    var interactive = ANGLES.indexOf(state.angle) !== -1;
    Object.keys(angleTransportHotspots).forEach(function (angleName) {
      var svg = angleTransportHotspots[angleName];
      if (svg) { svg.style.display = state.angle === angleName ? '' : 'none'; }
    });
    if (keyboardHotspotsSvg) { keyboardHotspotsSvg.style.display = state.angle === 'above' ? '' : 'none'; }
    document.dispatchEvent(new CustomEvent('eva:radio:angle', { detail: { angle: state.angle, interactive: interactive } }));
  }

  function cycleAngle() {
    var idx = ANGLES.indexOf(state.angle);
    state.angle = ANGLES[(idx + 1) % ANGLES.length];
    applyAngle();
    saveState();
  }

  function getTape(id) {
    for (var i = 0; i < tapes.length; i++) {
      if (tapes[i].id === id) { return tapes[i]; }
    }
    return null;
  }

  function currentTape() {
    return state.tapeId ? getTape(state.tapeId) : null;
  }

  // Phantom-tape fix (2026-08-07): state.tapeId/sideId PERSIST in
  // localStorage across reloads (see loadState() above) -- they really
  // mean "the last side ever deliberately selected," not "there is
  // physically a tape in the deck right now." A visitor who played a
  // tape days ago, then closed the tab, gets a fresh page load where the
  // RACK visually shows nothing inserted (eva-radio-tape.js's own
  // deckTapeId is in-memory only, never persisted, always starts null)
  // -- but state.tapeId/sideId are still sitting there from that old
  // session, so playWithSound() (the transport PLAY hotspot) would
  // happily resume/start that stale tape's audio. Per's rule: "empty
  // deck = PLAY produces no music, full stop" -- and per the rack's own
  // deckTapeId, the deck genuinely IS empty until a real insert happens
  // THIS session. deckLoaded is that missing signal: false at every
  // fresh module load regardless of what's persisted, flipped true only
  // by an actual insert (selectTapeSideAndPlay(), reached via the rack's
  // drag/PLAY-button/Enter through the public playTape() API) or by the
  // hard-reload resume arming itself (armHardLoadResume() below) --
  // which is the one legitimate case of "a tape really was left playing,
  // resume it," not a stale leftover. See playWithSound()'s own gate.
  var deckLoaded = false;

  function sideTracks(tape, sideId) {
    return (tape && tape.sides && tape.sides[sideId]) || [];
  }

  function trackAt(tape, sideId, index) {
    return sideTracks(tape, sideId)[index] || null;
  }

  // --- Sound-engine ducking ---------------------------------------------
  // This turns the UI hover/click sound VOLUME down while a tape is
  // audibly playing, via a small API sound-engine.js exposes on
  // window.EvaSoundEngine for exactly this purpose. Separate concern from
  // the global mute (see EvaMute.applyToElement(audio) in init() below) --
  // ducking is "quieter while something else plays," muting is "off
  // entirely," same distinction sound-engine.js's own duckFactor comment
  // draws.
  //
  // Also dispatches eva:radio:playstate: the radio widget lives outside
  // #MainContent and keeps playing straight through a PJAX navigation
  // (nothing about the swap ever touches it), which means it can end up
  // playing at the same time as a page-specific ambience loop -- the
  // Källare basement hum, currently the only other real-audio loop in
  // the theme -- with nothing coordinating the two. Broadcasting play/
  // stop as an event (rather than reaching into that block's internals
  // directly) keeps the radio widget from needing to know that page's
  // ambience even exists; the page listens in, not the other way round.
  function duckUiSound() {
    if (window.EvaSoundEngine && typeof window.EvaSoundEngine.duck === 'function') {
      window.EvaSoundEngine.duck(0.3);
    }
    document.dispatchEvent(new CustomEvent('eva:radio:playstate', { detail: { playing: true } }));
    document.dispatchEvent(new CustomEvent('eva:radio-play')); // Bolty's vibe-state hook (assets/bolty.js) -- separate from playstate above, kept minimal on purpose
    updateOnAirDot();
  }

  // reason ('pause' | 'stop') lets listeners (eva-radio-tape.js's deck
  // overlay) tell the two apart -- Per: pause freezes the inside-deck art
  // in place, stop fades it out. Both look identical from here (audio.
  // pause() either way) so the caller has to say which one this is.
  function restoreUiSound(reason) {
    if (window.EvaSoundEngine && typeof window.EvaSoundEngine.restore === 'function') {
      window.EvaSoundEngine.restore();
    }
    document.dispatchEvent(new CustomEvent('eva:radio:playstate', { detail: { playing: false, reason: reason } }));
    document.dispatchEvent(new CustomEvent('eva:radio-stop')); // Bolty's vibe-state hook (assets/bolty.js)
    updateOnAirDot();
  }

  // --- Media Session (lock-screen / control-center now-playing) ---------
  // Added 2026-08-09 -- navigator.mediaSession didn't exist ANYWHERE in
  // this codebase before this pass (confirmed by a full-repo search), so
  // the lock screen was falling back to document.title, which Shopify's
  // own seo-title.liquid renders as "Eva – Eva Hardware" on the
  // homepage -- not a mediaSession bug to fix, a mediaSession to add.
  // Mirrors eva-radio-station.js's own copy of this same pattern for
  // stations -- the two never fight over navigator.mediaSession because
  // they're already mutually exclusive by the SAME existing architecture
  // that keeps their audio exclusive (see that file's header comment):
  // whichever one is actually playing is always the one that most
  // recently wrote the metadata, no shared coordinator needed.
  //
  // Hooked from the audio element's own 'play' event (below), which
  // ALREADY fires for every case playback (re)starts -- a fresh
  // selectTapeSideAndPlay(), nextTrack() advancing within a side, and
  // flipToSide() all end in a call to play(), which fires this natively
  // -- so one hook point covers "metadata must update on tape change and
  // side flip" for free, no separate hooks needed at each call site.
  var mediaSessionSupported = 'mediaSession' in navigator;

  function updateTapeMediaSession() {
    if (!mediaSessionSupported) { return; }
    var tape = currentTape();
    if (!tape) { return; }
    // "include the side (A/B) if it fits naturally" -- appended to the
    // artist field since MediaMetadata has no dedicated side/episode
    // field, and artist is where a station's own name goes on the other
    // path, so putting the tape's own identity there too keeps the two
    // consistent (title always says "Eva Radio", artist always says
    // "what's actually playing").
    var sideLabel = state.sideId ? (' — Side ' + state.sideId.toUpperCase()) : '';
    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: 'Eva Radio',
        artist: (tape.title || 'Eva Radio') + sideLabel,
        // ITEM 3 AMENDMENT (2026-08-09): a dedicated square Eva Radio
        // artwork now, NOT the tape's own cover (tape.images.front) --
        // per-tape cover art is explicitly NOT wired here yet (tape
        // covers are portrait, the lock-screen slot is square, and the
        // crop/pad treatment is Per's own call to make later, not
        // assumed here). Same array used on the station side -- see
        // window.EvaRadioArtwork's own comment in theme.liquid.
        artwork: window.EvaRadioArtwork || []
      });
      navigator.mediaSession.playbackState = 'playing';
    } catch (e) {}
  }

  function setTapeMediaSessionPlaybackState(playbackState) {
    if (!mediaSessionSupported) { return; }
    try { navigator.mediaSession.playbackState = playbackState; } catch (e) {}
  }

  // Only a genuine STOP clears the lock-screen card entirely -- a PAUSE
  // (or a side simply ending) leaves the metadata in place and just
  // flips playbackState, same as any real media app's lock-screen
  // behavior (pausing a song doesn't make its card disappear).
  function clearTapeMediaSession() {
    if (!mediaSessionSupported) { return; }
    try {
      navigator.mediaSession.metadata = null;
      navigator.mediaSession.playbackState = 'none';
    } catch (e) {}
  }

  // --- Mechanical tape sounds (radio's own fixed-gain channel) -----------
  // Deliberately NOT routed through sound-engine.js's playSound()/
  // duckFactor -- these are the radio's own foreground sound effects,
  // not ambient UI blips, and don't get ducked by anything (they ARE
  // what's in the foreground when they play). They DO respect the global
  // mute now (EvaMute.applyToElement, see initMechSounds() below) --
  // "Eva Radio: music playback AND all mechanical sounds" per the
  // 2026-08-02 global-mute unification, superseding the exemption this
  // comment used to describe.
  //
  // RADIO_MECH_GAIN calibrated 2026-07-18 against the site's two
  // existing UI blip assets (sound-engine.js's hover/click, both played
  // at code-level volume 0.12 there): raw-file peak levels measured via
  // ffmpeg astats --
  //   ui-hover.mp3 peak -26.1dB, ui-click.mp3 peak -40.9dB
  //   eva_ui_tape_play/stop.wav peak -1.4dB, autostop -3.0dB, flip -3.8dB
  //     (average ~-2.4dB)
  // ~24-30dB louder than the existing assets at raw file level -- far
  // past the 4dB comfort threshold, so per spec the CHANNEL gain (not
  // the files) is scaled down, not re-encoded. Targeted the louder of
  // the two references (hover, -26dB) rather than click's near-silent
  // -41dB, since these are explicitly foreground mechanical cues, not
  // ambient blips -- matching hover's raw peak keeps them clearly
  // audible as intended while sitting in the same loudness neighborhood
  // as the loudest thing already on the page. 0.07 brings the four new
  // sounds to a peak of about -24.5dB to -26.9dB, within ~1.5dB of
  // hover's own -26.1dB.
  //
  // Note this compares raw FILE peaks, not "effective in-practice"
  // loudness after hover/click's own 0.12 code-level volume is applied
  // (which would put their in-practice peak near -44dB to -59dB --
  // matching THAT would make the tape sounds nearly inaudible, at odds
  // with the spec's own framing of them as "the foreground"). Flagged
  // here in case Per wants the alternate, much-quieter target instead.
  var RADIO_MECH_GAIN = 0.07;
  var RADIO_MECH_FF_REW_GAIN = RADIO_MECH_GAIN * 0.5; // "reuse the play clack at lower gain (0.5)" -- half of the tape channel's own base gain, not an absolute 0.5

  var mechPlayAudio = null;
  var mechStopAudio = null;
  var mechAutostopAudio = null;
  var mechFlipAudio = null;
  // Hold-seek sounds (FFW/REW wind while held, dedicated tap clacks) --
  // ONE pair of sounds covers BOTH directions: the reversed-audio
  // rew-specific files played noticeably quieter/duller than the
  // forward ones, so direction is communicated by the music itself
  // scrubbing backwards, not by a different wind sound. Per is still
  // preparing these two files; keys resolve to a 404 URL until they
  // land in assets/, which just means these no-op silently (play()
  // promise rejects, caught below) rather than break anything.
  var mechFfwWindAudio = null;
  var mechFfwTapAudio = null;

  function initMechSounds() {
    var assets = window.EvaRadioAssets || {};
    try {
      if (assets.mechPlay) { mechPlayAudio = new Audio(assets.mechPlay); mechPlayAudio.preload = 'auto'; }
      if (assets.mechStop) { mechStopAudio = new Audio(assets.mechStop); mechStopAudio.preload = 'auto'; }
      if (assets.mechAutostop) { mechAutostopAudio = new Audio(assets.mechAutostop); mechAutostopAudio.preload = 'auto'; }
      if (assets.mechFlip) { mechFlipAudio = new Audio(assets.mechFlip); mechFlipAudio.preload = 'auto'; }
      // mechFfwTap's own fallback (mechFfwTapAudio || mechPlayAudio, see
      // onSeekPointerUp) only works if a load failure actually nulls the
      // reference out -- a `new Audio(url)` for a URL that 404s is still
      // a truthy object right up until the browser fires its own 'error'
      // event, so without this listener the `||` never sees a falsy left
      // side and REW/FF's tap both silently try to play a broken file
      // forever instead of ever reaching the working play-clack fallback.
      if (assets.mechFfwWind) {
        mechFfwWindAudio = new Audio(assets.mechFfwWind);
        mechFfwWindAudio.preload = 'auto';
        mechFfwWindAudio.addEventListener('error', function () { mechFfwWindAudio = null; });
      }
      if (assets.mechFfwTap) {
        mechFfwTapAudio = new Audio(assets.mechFfwTap);
        mechFfwTapAudio.preload = 'auto';
        mechFfwTapAudio.addEventListener('error', function () { mechFfwTapAudio = null; });
      }
      if (window.EvaMute) {
        [mechPlayAudio, mechStopAudio, mechAutostopAudio, mechFlipAudio, mechFfwWindAudio, mechFfwTapAudio].forEach(function (a) {
          window.EvaMute.applyToElement(a);
        });
      }
    } catch (e) {}
  }

  // The 'error' listeners above null out mechFfwWindAudio/mechFfwTapAudio
  // once a load failure actually happens, but that's async -- an element
  // can still be a truthy, not-yet-failed Audio() in the brief window
  // before its own network request resolves. This is the synchronous
  // half of the same check: MediaError.prototype.error is set the moment
  // a failure is known, no event-loop turn required, so a tap/hold that
  // lands in that early window still gets the right answer instead of a
  // silent failed play() attempt.
  function workingAudio(mechAudio) {
    return (mechAudio && !mechAudio.error) ? mechAudio : null;
  }

  function playMech(mechAudio, gain) {
    if (!mechAudio) { return; }
    try {
      mechAudio.loop = false;
      mechAudio.pause();
      mechAudio.currentTime = 0;
      mechAudio.volume = gain;
      var p = mechAudio.play();
      if (p && typeof p.catch === 'function') { p.catch(function () {}); }
    } catch (e) {}
  }

  // Continuous wind sound while FFW/REW is held -- loops until stopMechLoop.
  function startMechLoop(mechAudio, gain) {
    if (!mechAudio) { return; }
    try {
      mechAudio.loop = true;
      mechAudio.currentTime = 0;
      mechAudio.volume = gain;
      var p = mechAudio.play();
      if (p && typeof p.catch === 'function') { p.catch(function () {}); }
    } catch (e) {}
  }

  function stopMechLoop(mechAudio) {
    if (!mechAudio) { return; }
    try {
      mechAudio.loop = false;
      mechAudio.pause();
      mechAudio.currentTime = 0;
    } catch (e) {}
  }

  // --- Positioning / drag -------------------------------------------------

  // Viewport dimensions, not document (2026-08-09) -- position:fixed means
  // x/y are viewport coordinates. This is also what actually fixes the
  // mobile overflow bug: window.innerWidth/innerHeight are properties of
  // the SCREEN, not of the page's own content, so they can't be inflated
  // by the very element being clamped against them the way
  // document.documentElement.scrollWidth could (see eva-radio.css's own
  // comment on .eva-radio-widget for the full incident writeup -- that
  // circularity, not merely "absolute vs fixed" in the abstract, was the
  // actual mechanism of the bug).
  function viewportWidth() {
    return window.innerWidth;
  }
  function viewportHeight() {
    return window.innerHeight;
  }

  // Mirrors eva-radio.css's own default-width rule (the plain
  // `.eva-radio-widget` 340px base, narrowed to `min(70vw, 300px)` under
  // the max-width:480px breakpoint) rather than reading it back off the
  // DOM -- once state.width is ever set, an inline style permanently
  // overrides the CSS rule, so there's no live "what would the CSS say"
  // value left to measure. This is pinch's 100% reference point: touch
  // scales from THIS (not from MIN_WIDTH, which is the desktop corner-
  // handle's own, unrelated floor). Keep the 480 literal in sync with
  // the CSS breakpoint if that ever changes.
  function naturalWidth() {
    return viewportWidth() <= 480 ? Math.min(viewportWidth() * 0.7, 300) : 340;
  }
  function effectiveMinWidth() {
    return IS_TOUCH_CAPABLE ? naturalWidth() : MIN_WIDTH;
  }
  function effectiveMaxWidth() {
    // Spec: touch scales 100%-200% of its own base; desktop keeps the
    // existing ~2x-of-CSS-default MAX_WIDTH constant.
    // 3.5x (Per, 2026-08-26; was 2x). This is the ONLY ceiling in the
    // widget -- grepped for other 2x assumptions and there are none, and
    // nothing in the layout is pinned to the old value: the frame is
    // width-driven (img width:100% -> height auto via intrinsic ratio),
    // hotspots are %-based against that same ratio, and the tape rack
    // sizes itself off the live eva:radio:resize rAF loop rather than
    // any constant. Overflowing the phone screen is expected and
    // allowed; staying reachable is clampPosition()'s job, and it
    // clamps on the CONTROL BAR only, so the ceiling can rise without
    // the controls ever leaving the screen.
    return IS_TOUCH_CAPABLE ? naturalWidth() * 3.5 : MAX_WIDTH;
  }

  // How far the resize handle, the RADIO mode-toggle tab, the minimize/
  // angle control-bar buttons, and (when rendered) the tape rack stick
  // out beyond the widget's OWN frame box, on each side -- measured live
  // via getBoundingClientRect() rather than hardcoded px/percent
  // guesses, so a future art/layout change can't silently desync the
  // clamp bounds the way a hardcoded reserve value already once did for
  // the rack (see RACK_RESERVE's own history/comment below). Each of
  // these elements is CSS-positioned relative to the widget's own box
  // (percentage/anchor offsets, not page coordinates), so the delta
  // between its rect and the widget's is the same regardless of where
  // the widget currently sits on screen -- safe to measure at the
  // widget's CURRENT position and reuse for a CANDIDATE position in
  // clampPosition() below. An element that isn't actually rendered
  // (hidden, display:none, or the rack tucked away at a breakpoint where
  // it needs no separate reserve) reports an all-zero rect and is
  // skipped automatically -- no separate visibility check needed.
  //
  // minimizeBtn/angleBtn added 2026-08-26: both sit at `top: -16px`
  // (eva-radio.css), 16px above the widget's own frame -- invisible
  // before this because clampPosition()'s minY used to be floored by
  // safeTop() (header height, itself far taller than 16px) regardless.
  // Now that the drag clamp no longer enforces safeTop() (Per: the
  // widget may be dragged over the header/nav now), these two are the
  // ONLY thing left keeping the control bar's own top edge on-screen at
  // an extreme upward drag/pinch -- the hard rule is "clamp on the
  // control bar, not the widget bounds," and this is what makes that
  // literally true rather than coincidentally true.
  function measureOverflow() {
    var wRect = widget.getBoundingClientRect();
    var overflow = { left: 0, right: 0, top: 0, bottom: 0 };
    [resizeHandle, radioToggleEl, tapeRackEl, minimizeBtn, angleBtn].forEach(function (el) {
      if (!el) { return; }
      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) { return; }
      overflow.left = Math.max(overflow.left, wRect.left - r.left);
      overflow.right = Math.max(overflow.right, r.right - wRect.right);
      overflow.top = Math.max(overflow.top, wRect.top - r.top);
      overflow.bottom = Math.max(overflow.bottom, r.bottom - wRect.bottom);
    });
    return overflow;
  }

  // Keeps the FULL assembly (widget + resize handle + RADIO tab + open
  // tape rack) fully inside the viewport on all four sides -- not just
  // "MIN_VISIBLE px of the widget itself," which is what let the old
  // document-relative version drift the widget (and the page along with
  // it) past the screen edge. Called on every drag/resize pointermove
  // tick (not just on release) -- overflow during the drag, even for a
  // single frame, is enough to trigger iOS's shrink-to-fit zoom, so the
  // clamp has to hold continuously, not just at the end.
  // The control bar's own box, measured relative to the widget's top-left
  // at the CURRENT scale: minimize, angle/reload and the RADIO tab. This is
  // what the hard rule protects -- "clamp on the control bar, never on the
  // widget body" -- so a widget deliberately zoomed past the screen edge can
  // still be panned freely as long as these stay reachable.
  function controlBarBounds() {
    var wRect = widget.getBoundingClientRect();
    var box = null;
    [minimizeBtn, angleBtn, radioToggleEl].forEach(function (el) {
      if (!el) { return; }
      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) { return; }
      var rel = {
        left: r.left - wRect.left, top: r.top - wRect.top,
        right: r.right - wRect.left, bottom: r.bottom - wRect.top
      };
      if (!box) { box = rel; return; }
      box.left = Math.min(box.left, rel.left);
      box.top = Math.min(box.top, rel.top);
      box.right = Math.max(box.right, rel.right);
      box.bottom = Math.max(box.bottom, rel.bottom);
    });
    return box;
  }

  function clampPosition(x, y) {
    var w = widget.offsetWidth;
    var h = widget.offsetHeight;
    var overflow = measureOverflow();

    // Preferred path: keep the CONTROL BAR on screen and let the body
    // overflow as far as it likes.
    var cb = controlBarBounds();
    if (cb) {
      // Each axis is a range [lo, hi]: lo keeps the control bar's near
      // edge from leaving one side, hi keeps its far edge from leaving
      // the other.
      //
      // WHEN THE CONTROL BAR NO LONGER FITS THE VIEWPORT, lo > hi and
      // the range inverts. Measured on the draft at 393px after the
      // ceiling went to 350%: the control-bar union box is 155px wide at
      // 100% but 427px at 350%, against a 393px viewport -- it is 34px
      // too wide to ever fit, so "all three controls on screen at once"
      // and "350% max" are geometrically incompatible on a phone this
      // narrow. That is not a tuning problem and no clamp can solve it.
      //
      // What a clamp CAN do is fail usefully. Written as a naive
      // max(lo, min(hi, x)) an inverted range collapses to a single
      // point -- x is pinned at lo no matter what the visitor does, so
      // the oversized widget cannot be panned AT ALL and the RADIO tab
      // is permanently unreachable. Normalising the range instead lets
      // the visitor pan the full width of the overflow and bring any
      // control they need under their thumb, which keeps the SPIRIT of
      // the rule (the control bar is always reachable) at the one scale
      // where its letter (always simultaneously visible) cannot hold.
      var loX = -cb.left, hiX = viewportWidth() - cb.right;
      var loY = -cb.top, hiY = viewportHeight() - cb.bottom;

      // Axis-by-axis: if this axis's range inverted, the control bar is
      // wider (or taller) than the viewport on it, and "all of it on
      // screen" is unsatisfiable. Swapping the bounds is NOT the answer
      // -- measured, that yields a range the size of the SHORTFALL (34px
      // at 350% on a 393px phone), which is worse than useless: it is
      // just enough to jitter and nowhere near enough to bring the far
      // control into view.
      //
      // Fall back to "keep at least CONTROL_BAR_MIN_VISIBLE_PX of the
      // control bar on screen" instead, which makes the whole overflow
      // pannable. Same 393px case: the range becomes 732px wide, and
      // every control -- minimize, angle and the RADIO tab alike -- can
      // be brought under the visitor's thumb. Always reachable, just not
      // always simultaneously visible.
      if (loX > hiX) {
        loX = -cb.right + CONTROL_BAR_MIN_VISIBLE_PX;
        hiX = viewportWidth() - cb.left - CONTROL_BAR_MIN_VISIBLE_PX;
      }
      if (loY > hiY) {
        loY = -cb.bottom + CONTROL_BAR_MIN_VISIBLE_PX;
        hiY = viewportHeight() - cb.top - CONTROL_BAR_MIN_VISIBLE_PX;
      }
      return {
        x: Math.max(loX, Math.min(hiX, x)),
        y: Math.max(loY, Math.min(hiY, y))
      };
    }

    // Fallback only if none of the control elements are measurable (e.g.
    // minimized/hidden): old whole-assembly behaviour.
    var minX = overflow.left;
    var maxX = viewportWidth() - w - overflow.right;
    // safeTop() (header/marquee) deliberately dropped from this floor
    // 2026-08-26 -- Per: the widget may now be dragged anywhere,
    // including over the nav/icon row/sticky header; the minimize
    // control is the intended way to get it out of the way, superseding
    // the earlier "never overlap page content/menu" rule. safeTop() is
    // still used below by defaultPosition() (fresh-visitor spawn only)
    // and by MAX_WIDTH's own desktop-collision comment. overflow.top
    // (resize handle/rack/minimize+angle buttons poking out the top,
    // see measureOverflow()'s own 2026-08-26 comment) is now the ONLY
    // floor -- and per the hard rule ("clamp on the control bar, not the
    // widget bounds"), it's the minimize/angle buttons' own overflow
    // that actually keeps the control bar on-screen here, not the
    // widget's photo/body.
    var minY = overflow.top;
    var maxY = viewportHeight() - h - overflow.bottom;
    return {
      // Math.max(minX, ...) wins if the assembly is wider than the
      // viewport itself (e.g. an extreme resize on a tiny screen) --
      // pins to the left/top-most valid edge rather than oscillating.
      // Resize clamping below keeps this a defensive fallback, not the
      // normal case.
      x: Math.max(minX, Math.min(maxX, x)),
      y: Math.max(minY, Math.min(maxY, y))
    };
  }

  // Fresh-visitor spawn: right side, below the header, clear of hero
  // CTAs -- anchored to the header's own bottom edge. viewport-relative
  // now (no more `+ window.scrollY`, 2026-08-09) to match position:fixed
  // -- a spawn computed in page coordinates would place the widget
  // wherever the header happened to be in the DOCUMENT, but a fixed
  // element's top/left are resolved against the VIEWPORT, so adding the
  // scroll offset would double-count it (this only ever mattered at
  // scrollY > 0, which first paint never is in practice, but the old
  // formula was wrong for the new coordinate system either way).
  function headerBottom() {
    var header = document.querySelector('.site-header, header[role="banner"], #shopify-section-header-group');
    if (!header) { return 140; }
    var rect = header.getBoundingClientRect();
    return rect.bottom;
  }

  // Desktop collision fix (2026-08-25): doubling MAX_WIDTH above made a
  // large widget overlap the header nav/LCD clock (both inside
  // .site-header, already covered by headerBottom()) AND the homepage
  // announcement marquee, which is NOT part of .site-header -- it's a
  // separate, page-optional section (sections/scrolling-text.liquid)
  // rendered as a sibling below it. Same "reserve a real UI strip, not
  // just the viewport edge" idea headerBottom()/defaultPosition() already
  // use, just extended to cover whichever of these two strips is
  // actually present and lower on THIS page -- an element that doesn't
  // exist on a given template (marquee is homepage-only) naturally drops
  // out via the same all-zero-rect-is-skipped pattern measureOverflow()
  // already relies on, no separate visibility check needed.
  //
  // Deliberately NOT extended to the product grid below: that's normal
  // scrollable page content, not a functional nav/utility strip that
  // becomes unusable when covered -- a widget resized to ~2x default
  // and parked just under the header/marquee is expected to overlap
  // some of the content beneath it, the same way it already could at
  // the old max size. Only the top strip is a hard floor.
  function safeTop() {
    var bottom = headerBottom();
    var marquee = document.querySelector('.scrolling-text');
    if (marquee) {
      var rect = marquee.getBoundingClientRect();
      if (rect.height) { bottom = Math.max(bottom, rect.bottom); }
    }
    return bottom;
  }

  function defaultPosition() {
    var w = widget.offsetWidth || 340;
    // Only reserve room for the rack above the same 700px breakpoint
    // eva-radio.css uses to move the rack BELOW the widget instead of
    // beside it (see RACK_RESERVE's comment) -- below that breakpoint
    // the rack already needs no extra horizontal room, so reserving it
    // there would just push the widget needlessly further left on
    // small/narrow viewports for no reason.
    var reserve = window.innerWidth > 700 ? RACK_RESERVE : 0;
    return {
      x: viewportWidth() - w - MARGIN - reserve,
      y: safeTop() + MARGIN
    };
  }

  function applyPosition(x, y) {
    widget.style.left = x + 'px';
    widget.style.top = y + 'px';
    widget.style.right = 'auto';
    widget.style.bottom = 'auto';
  }

  function applyWidth() {
    if (state.width) {
      widget.style.width = state.width + 'px';
    }
    // else: leave the CSS default (responsive) in place.
  }

  function positionWidget() {
    applyWidth();
    var pos;
    if (state.userMoved && state.x !== null && state.y !== null) {
      pos = clampPosition(state.x, state.y);
    } else {
      pos = defaultPosition();
    }
    state.x = pos.x;
    state.y = pos.y;
    applyPosition(pos.x, pos.y);
  }

  var dragging = false;
  var dragMoved = false;
  var dragStartX = 0;
  var dragStartY = 0;
  var widgetStartX = 0;
  var widgetStartY = 0;
  var activePointerId = null;

  function onPointerDown(e) {
    // Hotspots and the bubble/minimize/resize controls handle their own
    // clicks -- don't let a drag start swallow those taps.
    if (e.target.closest && e.target.closest('.eva-radio-hotspot, .eva-radio-widget__bubble-close, .eva-radio-widget__bubble-action, .eva-radio-widget__minimize, .eva-radio-widget__resize-handle')) {
      return;
    }
    dragging = true;
    dragMoved = false;
    activePointerId = e.pointerId;
    dragStartX = e.clientX;
    dragStartY = e.clientY;
    widgetStartX = state.x;
    widgetStartY = state.y;
    try { dragHandle.setPointerCapture(e.pointerId); } catch (err) {}
  }

  function onPointerMove(e) {
    if (!dragging || e.pointerId !== activePointerId) { return; }
    var dx = e.clientX - dragStartX;
    var dy = e.clientY - dragStartY;
    if (!dragMoved && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
      dragMoved = true;
    }
    if (!dragMoved) { return; }
    var pos = clampPosition(widgetStartX + dx, widgetStartY + dy);
    state.x = pos.x;
    state.y = pos.y;
    applyPosition(pos.x, pos.y);
  }

  function onPointerUp(e) {
    if (!dragging || e.pointerId !== activePointerId) { return; }
    dragging = false;
    activePointerId = null;
    try { dragHandle.releasePointerCapture(e.pointerId); } catch (err) {}
    if (dragMoved) {
      state.userMoved = true;
      saveState();
    }
  }

  // --- Resize -------------------------------------------------------

  var resizing = false;
  var resizeStartX = 0;
  var resizeStartWidth = 0;
  var resizeActivePointerId = null;

  function onResizePointerDown(e) {
    // Second, independent guard on the same class of bug as the binding
    // above: even if these listeners are somehow attached on a touch
    // device (a future refactor, a device the feature-detect misses), a
    // touch pointer must never drive the single-pointer width math
    // below. Touch resizing is pinch's job and pinch's alone.
    if (e.pointerType === 'touch') { return; }
    resizing = true;
    resizeActivePointerId = e.pointerId;
    resizeStartX = e.clientX;
    resizeStartWidth = widget.offsetWidth;
    try { resizeHandle.setPointerCapture(e.pointerId); } catch (err) {}
    // Rack-desync fix (2026-08-06): tells eva-radio-tape.js a continuous
    // scale gesture is starting, so it can suppress its own CSS
    // transitions and drive tape sizing/alignment off a live rAF loop
    // instead -- see that file's own eva:radio:resize listener for why
    // (transitionend alone only fires on settle, never mid-drag).
    document.dispatchEvent(new CustomEvent('eva:radio:resize', { detail: { active: true } }));
  }

  function onResizePointerMove(e) {
    if (!resizing || e.pointerId !== resizeActivePointerId) { return; }
    var dx = e.clientX - resizeStartX;
    var newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, resizeStartWidth + dx));
    // Spec: "the widget cannot be resized larger than the viewport" --
    // caps width so that, even fully repositioned against the nearest
    // edge, the assembly (widget + resize handle/RADIO tab overflow)
    // still fits. Reuses the PRE-resize overflow measurement as a close
    // approximation -- these satellites' offsets are small relative to
    // the widget's own width and don't meaningfully shift across a
    // single resize gesture, so re-deriving them from a not-yet-applied
    // width isn't worth the extra complexity.
    var overflow = measureOverflow();
    var maxWidthForViewport = Math.max(MIN_WIDTH, viewportWidth() - overflow.left - overflow.right);
    newWidth = Math.min(newWidth, maxWidthForViewport);
    widget.style.width = newWidth + 'px';
    // Hotspots are %-based against the image's own aspect ratio, which
    // cascades from this width automatically (img width:100% -> height
    // auto via intrinsic ratio -> hotspot SVG height:100% of that) --
    // no separate height/hotspot recalculation needed here. Tape rack
    // sizing/alignment tracks this via its own rAF loop (see
    // eva-radio-tape.js's eva:radio:resize listener) rather than being
    // driven from here directly -- keeps this module ignorant of the
    // rack subsystem, same separation as every other cross-file event.

    // Clamp on every tick, not only on release (spec) -- growing the box
    // can push its right/bottom edge past the viewport before MAX_WIDTH
    // (or the cap above) is even reached, if the widget was already
    // parked near an edge. Reuses the same clampPosition() drag uses.
    var pos = clampPosition(state.x, state.y);
    state.x = pos.x;
    state.y = pos.y;
    applyPosition(pos.x, pos.y);
  }

  function onResizePointerUp(e) {
    if (!resizing || e.pointerId !== resizeActivePointerId) { return; }
    resizing = false;
    resizeActivePointerId = null;
    try { resizeHandle.releasePointerCapture(e.pointerId); } catch (err) {}
    document.dispatchEvent(new CustomEvent('eva:radio:resize', { detail: { active: false } }));
    state.width = widget.offsetWidth;
    // Already clamped on every tick above -- this is just the final
    // state-persistence snapshot, not load-bearing for overflow.
    var pos = clampPosition(state.x, state.y);
    state.x = pos.x;
    state.y = pos.y;
    applyPosition(pos.x, pos.y);
    saveState();
  }

  // --- Pinch-to-zoom (touch only, 2026-08-26) -------------------------------
  // Full rebuild, not a restore of b520551 (reverted same day for an
  // undiagnosed real-device crash). Differences from that version, both
  // deliberate:
  //   1. Scales 100%-200% of naturalWidth() (the CSS default for the
  //      CURRENT viewport), not MIN_WIDTH..MAX_WIDTH -- those are the
  //      desktop corner-handle's own constants, and MAX_WIDTH is now
  //      680px (~2x a 340px DESKTOP default), meaningless as a touch
  //      ceiling on a ~300px mobile default.
  //   2. Anchors at the pinch MIDPOINT (spec: "the detail under the
  //      fingers stays under the fingers") -- b520551 only ever resized
  //      from the top-left corner, same as the drag handle. See
  //      updatePinch()'s own comment for the math.
  //   3. Hardened against the likely crash cause: if iOS's own gesture
  //      recognizer (Control Center swipe from the top-right, app-
  //      switcher swipe-up, the lock button itself) steals an in-progress
  //      two-finger touch, neither pointerup NOR pointercancel is
  //      guaranteed to reach the page -- pinching would stay stuck true
  //      forever, and eva-radio-tape.js's liveResizeTick() rAF loop
  //      (started by the eva:radio:resize dispatch below) would spin
  //      indefinitely, burning battery/CPU until the tab itself became
  //      unresponsive. The visibilitychange listener at the end of this
  //      section force-ends any stuck pinch the instant the page is
  //      backgrounded, regardless of which/whether a pointer event ever
  //      arrives.
  //
  // Bound on `widget` itself (not dragHandle), same reasoning b520551
  // had: "two fingers on the boombox" should count even if one of them
  // happens to be over a hotspot -- onPinchTrackPointerDown/-Move/-Up
  // track every active touch pointer's last position in touchPoints; the
  // SECOND simultaneous touch pointer is what flips into pinch mode,
  // force-cancelling any single-finger drag already under way from the
  // first finger so drag and pinch never fight over the same gesture.
  var touchPoints = {};       // pointerId -> {x, y}, touch-type pointers currently down anywhere on the widget
  var pinching = false;
  var pinchPointerIds = [];   // the exact two ids the active pinch is locked to
  var pinchStartDist = 0;
  var pinchStartWidth = 0;
  var pinchStartX = 0;        // widget's state.x/y at gesture start (pre-move)
  var pinchStartY = 0;
  var pinchAnchorRelX = 0;    // pinch midpoint's offset from the widget's top-left AT GESTURE START, in start-of-gesture (unscaled) px -- "which point on the widget is under the fingers," held constant through the gesture
  var pinchAnchorRelY = 0;

  function pointDistance(a, b) {
    var dx = a.x - b.x, dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function pointMid(a, b) {
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }

  function startPinch(ids) {
    pinching = true;
    pinchPointerIds = ids.map(Number);
    var p0 = touchPoints[pinchPointerIds[0]], p1 = touchPoints[pinchPointerIds[1]];
    pinchStartDist = Math.max(1, pointDistance(p0, p1));
    pinchStartWidth = widget.offsetWidth;
    pinchStartX = state.x;
    pinchStartY = state.y;
    var mid = pointMid(p0, p1);
    pinchAnchorRelX = mid.x - pinchStartX;
    pinchAnchorRelY = mid.y - pinchStartY;
    // A single-finger drag from the first finger may already be under
    // way (dragHandle's own onPointerDown) -- stop it cleanly rather
    // than leaving it to keep repositioning the widget underneath the
    // pinch. The corner handle can't be mid-resize from touch input
    // (hidden/unbound whenever IS_TOUCH_CAPABLE, see init below), so
    // `resizing` is never true here in practice; guarded anyway for
    // symmetry/safety.
    if (dragging) {
      dragging = false;
      try { dragHandle.releasePointerCapture(activePointerId); } catch (err) {}
      activePointerId = null;
    }
    if (resizing) {
      resizing = false;
      try { resizeHandle.releasePointerCapture(resizeActivePointerId); } catch (err) {}
      resizeActivePointerId = null;
    }
    // Same rack-desync fix the corner handle uses (onResizePointerDown's
    // own comment) -- tells eva-radio-tape.js a continuous scale gesture
    // is starting so it drives tape sizing/alignment off a live rAF loop
    // instead of waiting for transitionend, which never fires mid-drag.
    document.dispatchEvent(new CustomEvent('eva:radio:resize', { detail: { active: true } }));
  }

  function updatePinch() {
    var p0 = touchPoints[pinchPointerIds[0]], p1 = touchPoints[pinchPointerIds[1]];
    if (!p0 || !p1) { return; }
    var mid = pointMid(p0, p1);
    var rawScale = pointDistance(p0, p1) / pinchStartDist;
    var desiredWidth = pinchStartWidth * rawScale;
    var newWidth = Math.max(effectiveMinWidth(), Math.min(effectiveMaxWidth(), desiredWidth));
    // NO viewport cap here any more (2026-08-26, Per). Overflowing the
    // screen at 200% is the POINT of pinch: he zooms in to inspect the
    // transport buttons and the deck up close, then pans the oversized
    // widget around. The old cap silently pinned growth at viewport width
    // -- measured on the draft at 393px, a 275px widget could only reach
    // 388px before stopping dead, which is exactly the "cannot grow past
    // the phone screen" symptom. Staying reachable is now the CONTROL
    // BAR's job (see clampPosition), not the widget body's.
    // The REAL (possibly clamped) scale actually being applied this
    // frame -- may differ from rawScale once either clamp above kicks
    // in, and the position math below has to use this one, not
    // rawScale, or the anchor point would drift off the fingers right
    // at the 100%/200%/viewport ceiling.
    var appliedScale = newWidth / pinchStartWidth;
    widget.style.width = newWidth + 'px';
    // Anchor at the pinch midpoint: the point that was under the fingers
    // at gesture start (pinchAnchorRel*, in start-of-gesture px, i.e.
    // "40% of the way across the widget") has to land back under the
    // CURRENT finger midpoint after scaling by appliedScale -- standard
    // pinch-zoom anchor math, doubling as pan since `mid` also tracks
    // any incidental finger movement, not just spread.
    var pos = clampPosition(mid.x - pinchAnchorRelX * appliedScale, mid.y - pinchAnchorRelY * appliedScale);
    state.x = pos.x;
    state.y = pos.y;
    applyPosition(pos.x, pos.y);
  }

  function endPinch() {
    pinching = false;
    pinchPointerIds = [];
    document.dispatchEvent(new CustomEvent('eva:radio:resize', { detail: { active: false } }));
    state.width = widget.offsetWidth;
    // Already clamped on every tick above -- this is just the final
    // state-persistence snapshot, not load-bearing for overflow. Both
    // state.width and state.x/y (set continuously throughout the
    // gesture, same as the drag/resize handlers above) are what
    // "persist scale/position across PJAX" actually rides on -- the
    // widget's own DOM lives outside #MainContent so it's never torn
    // down, and this same `state` object/localStorage entry is what
    // positionWidget()/applyWidth() read from on any later restore.
    var pos = clampPosition(state.x, state.y);
    state.x = pos.x;
    state.y = pos.y;
    applyPosition(pos.x, pos.y);
    saveState();
  }

  function onPinchTrackPointerDown(e) {
    if (e.pointerType !== 'touch') { return; }
    touchPoints[e.pointerId] = { x: e.clientX, y: e.clientY };
    var ids = Object.keys(touchPoints);
    if (ids.length === 2 && !pinching) { startPinch(ids); }
    // A 3rd+ finger is tracked (so its later pointerup doesn't wrongly
    // read as "the pinch ended") but never changes which two ids the
    // active pinch is locked to.
  }

  function onPinchTrackPointerMove(e) {
    if (e.pointerType !== 'touch' || !touchPoints[e.pointerId]) { return; }
    touchPoints[e.pointerId] = { x: e.clientX, y: e.clientY };
    if (!pinching) { return; }
    // .eva-radio-widget already carries touch-action:none, but a real
    // phone's native pinch-zoom is exactly what spec calls out to guard
    // against -- belt and braces, requires the listener below to be
    // non-passive.
    e.preventDefault();
    updatePinch();
  }

  function onPinchTrackPointerUp(e) {
    if (e.pointerType !== 'touch') { return; }
    delete touchPoints[e.pointerId];
    if (pinching && pinchPointerIds.indexOf(e.pointerId) !== -1) { endPinch(); }
  }

  // Crash hardening (see this section's own header comment, point 3):
  // force-ends a stuck pinch the moment the page is backgrounded
  // (Control Center, app-switcher, the lock button, a real screen lock),
  // regardless of whether either finger's pointerup/pointercancel ever
  // actually reaches the page -- iOS's own gesture recognizer can steal
  // a two-finger touch sequence outright. Without this, `pinching` could
  // stay true forever, leaving eva-radio-tape.js's live-resize rAF loop
  // running indefinitely in the background.
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) { return; }
    if (pinching) { endPinch(); }
    touchPoints = {};
  });

  // Re-clamps both size and position against the CURRENT viewport --
  // shared by the window resize listener and the orientationchange
  // listener below (spec: "so a widget parked at the right edge in
  // landscape does not sit offscreen in portrait"). Width can genuinely
  // need to SHRINK here (not just reposition) -- a widget resized to
  // 560px wide on a landscape viewport can no longer fit at all on a
  // narrower portrait one, however far left it's pushed.
  function reclampWidgetToViewport() {
    if (!widget || widget.hidden) { return; }
    // WIDTH IS NO LONGER TOUCHED HERE (2026-08-26, Per: "a drag must never
    // change size"). This used to shrink the widget whenever it was wider
    // than the viewport -- and on iOS that fires constantly during a drag,
    // because showing/hiding the URL bar resizes the visual viewport. The
    // result was a widget that got smaller every time it was dragged and
    // never grew back, which is the reported "dragging left makes it
    // smaller". Size is now changed by pinch and the desktop corner handle
    // ONLY. This function repositions; it does not resize.
    var pos = clampPosition(state.x, state.y);
    state.x = pos.x;
    state.y = pos.y;
    applyPosition(pos.x, pos.y);
    saveState();
  }

  window.addEventListener('resize', reclampWidgetToViewport);
  // Some browsers fire 'resize' late/inconsistently around an
  // orientation flip (the visual viewport can settle a frame or two
  // after 'resize' already fired with stale dimensions) -- a dedicated
  // listener plus a short follow-up timeout covers both without
  // depending on either firing in a particular order.
  window.addEventListener('orientationchange', function () {
    reclampWidgetToViewport();
    setTimeout(reclampWidgetToViewport, 300);
  });

  // --- Open / close --------------------------------------------------

  function updateToggleButtonsUI() {
    toggleBtns.forEach(function (btn) {
      btn.setAttribute('aria-pressed', state.minimized ? 'false' : 'true');
    });
  }

  // On-air dot: the only header-visible sign a tape is still playing
  // once the widget chassis itself is minimized. Never shown while the
  // widget is visible (restored) -- the widget itself is evidence
  // enough there, this is specifically for the minimized+playing case.
  function updateOnAirDot() {
    var showDot = state.playing && state.minimized;
    onAirDots.forEach(function (dot) {
      dot.hidden = !showDot;
    });
  }

  function restoreWidget() {
    state.minimized = false;
    widget.hidden = false;
    positionWidget();
    updateToggleButtonsUI();
    updateOnAirDot();
    saveState();
    // Phase 2 visibility model (2026-08-04): "tapes shown on open" --
    // eva-radio-tape.js owns the tape rack directly and listens for this
    // to reset any hidden-while-playing state, same cross-file event
    // pattern eva:radio:playstate/eva:radio:angle already use.
    document.dispatchEvent(new CustomEvent('eva:radio:opened'));
  }

  function minimizeWidget() {
    state.minimized = true;
    widget.hidden = true;
    updateToggleButtonsUI();
    updateOnAirDot();
    saveState();
  }

  function toggleMinimized() {
    if (state.minimized) { restoreWidget(); } else { minimizeWidget(); }
  }

  // --- Speech bubble: end-of-side prompt only ---------------------------
  // The old persistent first-open "info" hint is gone -- all control
  // hints are hover tooltips now (see the tooltip chip below). This
  // bubble is purely event-driven: it only ever shows the end-of-side
  // flip prompt.

  function hideBubble() {
    bubble.hidden = true;
    bubbleAction.hidden = true;
    bubbleAction.onclick = null;
  }

  function dismissBubble() {
    hideBubble();
  }

  // End-of-side: the physical gesture (eject -> flip -> reinsert, see
  // eva-radio-tape.js) is the PRIMARY path now, so this is informational
  // rather than a question -- the button is a small accessible fallback
  // for anyone who can't do the drag/tap gesture, not the main call to
  // action.
  function showSidePrompt(sideId) {
    if (sideId === 'a') {
      bubbleText.innerHTML = 'End of Side A &mdash; flip the tape.';
      bubbleAction.textContent = '[ flip & play Side B ]';
      bubbleAction.onclick = function () { flipToSide('b'); };
    } else {
      bubbleText.innerHTML = 'End of Side B &mdash; flip the tape.';
      bubbleAction.textContent = '[ flip & play Side A ]';
      bubbleAction.onclick = function () { flipToSide('a'); };
    }
    bubbleAction.hidden = false;
    bubble.hidden = false;
  }

  // --- Tooltip chip -------------------------------------------------------
  // One reusable element for every control hint (deck buttons, door, tone
  // toggle, rhythm presets, the tape object) -- shown/positioned against
  // whichever element is currently hovered/touched, driven by a single
  // delegated listener pair below rather than per-control wiring. Exposed
  // on window.EvaRadioWidget so eva-radio-tape.js's tape object (a
  // different file, outside dragHandle) can reuse the same chip.
  var tooltipEl = null;
  var tooltipTextEl = null;
  var TOOLTIP_OFFSET = 8;
  var tooltipTouchedOnce = null; // Set of elements a touch tooltip already fired for this page load

  function positionTooltip(targetRect) {
    var tw = tooltipEl.offsetWidth;
    var th = tooltipEl.offsetHeight;
    var left = targetRect.left + targetRect.width / 2 - tw / 2;
    left = Math.max(4, Math.min(left, window.innerWidth - tw - 4));
    var top = targetRect.bottom + TOOLTIP_OFFSET;
    if (top + th > window.innerHeight - 4) {
      top = targetRect.top - th - TOOLTIP_OFFSET; // flip above -- only when there's not enough viewport room below
    }
    tooltipEl.style.left = left + 'px';
    tooltipEl.style.top = top + 'px';
  }

  function showTooltip(targetEl, text) {
    if (!tooltipEl || !text) { return; }
    tooltipTextEl.textContent = text;
    tooltipEl.hidden = false;
    positionTooltip(targetEl.getBoundingClientRect());
  }

  function hideTooltip() {
    if (tooltipEl) { tooltipEl.hidden = true; }
  }

  // --- Cassette door -----------------------------------------------------
  // Per: pressing the door must never pop up a list -- it only opens or
  // closes the tape rack (2026-08-09: a real toggle, same target both
  // ways; previously only ever opened, same as the rack's own hatch
  // tab), nothing else. The rack itself lives in eva-radio-tape.js
  // (see its own toggleRackFromDoor()), so this just broadcasts the
  // request rather than reaching into that module directly.
  function requestShowRack() {
    hideBubble(); // pressing the door counts as engaging with the widget
    document.dispatchEvent(new CustomEvent('eva:radio:door:pressed'));
  }

  // --- Playback ----------------------------------------------------------

  function loadTrack(tape, sideId, index, resumeSeconds) {
    var track = trackAt(tape, sideId, index);
    if (!track || !track.url) { return false; }
    audio.src = track.url;
    audio.currentTime = resumeSeconds || 0;
    currentTrackGainDb = typeof track.gainDb === 'number' ? track.gainDb : 0;
    updateAudioVolume(); // apply the new track's loudness-normalization gain immediately
    return true;
  }

  // Selects a side and starts it playing immediately, with NO mechanical
  // play-clack of its own -- used both for a fresh rack insert (which DOES
  // get the clack, via selectTapeSide() below calling this after the
  // clack) and for the flip sequence (which must NOT double up on a
  // clack, since eva_ui_tape_flip.wav already contains its own play-press
  // sound per spec).
  function selectTapeSideAndPlay(tapeId, sideId) {
    var tape = getTape(tapeId);
    if (!tape) { return; }
    deckLoaded = true; // a genuine insert THIS session -- see deckLoaded's own comment
    state.tapeId = tapeId;
    state.sideId = sideId;
    state.trackIndex = 0;
    state.positionSec = 0;
    // The tape object (eva-radio-tape.js) tracks its own flipped-face state
    // locally, but the accessible flip-and-play bubble fallback changes
    // the side through THIS function too, not through the tape object's
    // own insert gesture -- broadcast so the physical tape stays visually
    // in sync with whichever side is actually playing.
    document.dispatchEvent(new CustomEvent('eva:radio:tape:side', { detail: { tapeId: tapeId, sideId: sideId } }));
    var trackList = sideTracks(tape, sideId);
    if (!trackList.length) {
      // Empty/not-yet-populated side: remember the selection, nothing to play.
      saveState();
      return;
    }
    loadTrack(tape, sideId, 0, 0);
    play();
  }

  function selectTapeSide(tapeId, sideId) {
    dismissBubble();
    playMech(mechPlayAudio, RADIO_MECH_GAIN);
    setTimeout(function () {
      selectTapeSideAndPlay(tapeId, sideId);
    }, PLAY_SOUND_LEAD_MS);
  }

  function play() {
    var tape = currentTape();
    if (!tape || !state.sideId) { return; } // nothing selected -- graceful no-op
    if (!sideTracks(tape, state.sideId).length) { return; }
    if (!audio.src) {
      loadTrack(tape, state.sideId, state.trackIndex, state.positionSec);
    }
    // Take the music floor before making a sound -- this is what stops
    // the Basement TV when a tape starts. Not marked auto: reaching
    // play() means the visitor pressed something, so it outranks a
    // playing film by design (Per: starting either pauses the other).
    if (window.EvaAudioPolicy) { window.EvaAudioPolicy.claim('radio'); }
    var p = audio.play();
    if (p && typeof p.catch === 'function') { p.catch(function () {}); }
  }

  // PLAY hotspot specifically (resuming an already-selected side): clack
  // first, music enters ~150ms into it. A fresh rack insert gets the
  // same clack via selectTapeSide() above instead.
  function playWithSound() {
    var tape = currentTape();
    if (!tape || !state.sideId) { return; }
    playMech(mechPlayAudio, RADIO_MECH_GAIN);
    // Phantom-tape fix (2026-08-07): `tape` above only proves state.tapeId
    // is set, which can be a stale leftover from a PAST session with
    // nothing genuinely inserted THIS one -- see deckLoaded's own
    // comment. Per's rule: empty deck = PLAY produces no music, at MOST
    // a mechanical click -- the clack above still plays either way (the
    // deck acknowledges the press, same as a real one would), but the
    // actual music is gated here.
    if (!deckLoaded) { return; }
    setTimeout(play, PLAY_SOUND_LEAD_MS);
  }

  function pause() {
    cancelSeekHold(); // never leave the deck stuck sped-up/ducked if pause lands mid-hold
    if (window.EvaAudioPolicy) { window.EvaAudioPolicy.release('radio'); }
    audio.pause();
    state.playing = false;
    state.positionSec = audio.currentTime || 0;
    restoreUiSound('pause');
    saveState();
  }

  function pauseWithSound() {
    playMech(mechStopAudio, RADIO_MECH_GAIN);
    pause();
  }

  function stop() {
    cancelSeekHold();
    if (window.EvaAudioPolicy) { window.EvaAudioPolicy.release('radio'); }
    audio.pause();
    audio.currentTime = 0;
    state.playing = false;
    state.positionSec = 0;
    restoreUiSound('stop');
    clearTapeMediaSession();
    saveState();
  }

  function stopWithSound() {
    playMech(mechStopAudio, RADIO_MECH_GAIN);
    stop();
  }

  function seek(deltaSeconds) {
    var tape = currentTape();
    if (!tape || !state.sideId || !audio.src) { return; }
    var next = audio.currentTime + deltaSeconds;
    if (next < 0) { next = 0; } // REW at/near the start of a track just restarts it
    if (audio.duration && next > audio.duration) {
      nextTrack(); // stays within the side -- nextTrack() itself won't cross a side boundary
      return;
    }
    audio.currentTime = next;
  }

  // --- FF / REW: tap (fixed step) vs hold (authentic tape scrub) --------
  // A tap is a single fixed jump. A HOLD keeps the tape "moving": FFW
  // keeps the music audible sped up (pitch rising, like a real deck
  // freewheeling), REW effectively lifts the head (ducked near-silent)
  // while scrubbing backwards in visible chunks. Distinguished purely by
  // how long the hotspot stays pressed -- SEEK_HOLD_THRESHOLD_MS decides
  // which one a given press turns out to be, decided at release, not at
  // press time.
  var seekHoldTimer = null;
  var seekHoldAction = null; // null | 'ff' | 'rew'
  var seekHoldPointerId = null;
  var rewScrubInterval = null;

  // Composed tape volume: FFW/REW's near-silent duck always wins while
  // held (matches prior behavior exactly); otherwise the light keyboard
  // duck applies if any key is currently held, else full volume. Keeping
  // this as one function (rather than each caller setting audio.volume
  // directly) avoids the two ducks racing to stomp on each other's
  // restore -- e.g. releasing a key mid-FFW-hold must not un-duck FFW.
  // The per-track gainDb (EBU R128 loudness-normalization, set by
  // loadTrack()) is applied multiplicatively on top of whichever duck
  // wins, so a quiet-mastered track isn't left buried under a duck sized
  // for full-volume playback.
  var keyboardDucking = false;
  var currentTrackGainDb = 0;
  function dbToLinear(db) { return Math.pow(10, db / 20); }
  function updateAudioVolume() {
    var trackGain = dbToLinear(currentTrackGainDb || 0);
    var base;
    if (seekHoldAction === 'ff') { base = FFW_DUCK_VOLUME; }
    else if (seekHoldAction === 'rew') { base = REW_DUCK_VOLUME; }
    else { base = keyboardDucking ? KEYBOARD_DUCK_VOLUME : 1; }
    audio.volume = Math.max(0, Math.min(1, base * trackGain));
  }

  // Lets eva-radio-tape.js sync the spool hubs to the transport's actual
  // state (fast-forward/rewind spin during a hold, back to normal
  // play/pause/stop once released) -- see that file's updateDeckWindowEffects().
  function dispatchSeekState(action) {
    document.dispatchEvent(new CustomEvent('eva:radio:seekstate', { detail: { action: action } }));
  }

  function beginSeekHold(action) {
    seekHoldAction = action;
    dispatchSeekState(action);
    if (isTapeDevMode()) { console.debug('[eva-radio] ' + action + ' hold start: duck volume', action === 'ff' ? FFW_DUCK_VOLUME : REW_DUCK_VOLUME, 'windSound', workingAudio(mechFfwWindAudio) ? workingAudio(mechFfwWindAudio).src : '(missing, silent)'); }
    if (action === 'ff') {
      audio.playbackRate = FFW_PLAYBACK_RATE;
      audio.preservesPitch = false;
      audio.webkitPreservesPitch = false;
      audio.mozPreservesPitch = false;
      updateAudioVolume();
      startMechLoop(workingAudio(mechFfwWindAudio), RADIO_MECH_GAIN); // no fallback loop sound exists yet -- silent (not a broken play() attempt) until Per supplies the file, same for both directions
    } else if (action === 'rew') {
      updateAudioVolume();
      startMechLoop(workingAudio(mechFfwWindAudio), RADIO_MECH_GAIN); // same wind sound as FFW -- direction reads from the music scrubbing backwards, not the sound
      rewScrubInterval = setInterval(function () {
        seek(-REW_SCRUB_STEP_SECONDS);
      }, REW_SCRUB_INTERVAL_MS);
    }
  }

  // Cleanup shared by a normal release and an abnormal one (pointercancel,
  // or a stop/pause landing mid-hold) -- always restores rate/volume and
  // silences the wind loop, never leaves the deck stuck sped-up or ducked.
  function endSeekHold() {
    if (!seekHoldAction) { return; }
    if (seekHoldAction === 'ff') {
      audio.playbackRate = 1;
      audio.preservesPitch = true;
      audio.webkitPreservesPitch = true;
      audio.mozPreservesPitch = true;
      stopMechLoop(mechFfwWindAudio);
    } else if (seekHoldAction === 'rew') {
      if (rewScrubInterval) { clearInterval(rewScrubInterval); rewScrubInterval = null; }
      stopMechLoop(mechFfwWindAudio);
    }
    seekHoldAction = null;
    dispatchSeekState(null);
    updateAudioVolume();
  }

  function cancelSeekHold() {
    seekHoldPointerId = null;
    if (seekHoldTimer) { clearTimeout(seekHoldTimer); seekHoldTimer = null; }
    endSeekHold();
  }

  function onSeekPointerDown(action) {
    return function (e) {
      dismissBubble();
      seekHoldPointerId = e.pointerId;
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) {}
      seekHoldTimer = setTimeout(function () {
        seekHoldTimer = null;
        beginSeekHold(action);
      }, SEEK_HOLD_THRESHOLD_MS);
    };
  }

  function onSeekPointerUp(action) {
    return function (e) {
      if (e.pointerId !== seekHoldPointerId) { return; }
      seekHoldPointerId = null;
      if (seekHoldTimer) {
        // Released before the hold threshold fired -- a tap, not a hold.
        clearTimeout(seekHoldTimer);
        seekHoldTimer = null;
        var tapAudio = workingAudio(mechFfwTapAudio) || mechPlayAudio; // same tap sound for both directions; falls back to the play clack until it lands
        if (isTapeDevMode()) { console.debug('[eva-radio] ' + action + ' tap sound:', tapAudio && tapAudio.src); }
        playMech(tapAudio, RADIO_MECH_FF_REW_GAIN);
        seek(action === 'ff' ? SEEK_SECONDS : -SEEK_SECONDS);
      } else {
        endSeekHold();
      }
    };
  }

  function onSeekPointerCancel(e) {
    if (e.pointerId !== seekHoldPointerId) { return; }
    cancelSeekHold();
  }

  function nextTrack() {
    var tape = currentTape();
    if (!tape || !state.sideId) { return; }
    var next = state.trackIndex + 1;
    if (next >= sideTracks(tape, state.sideId).length) {
      endOfSide();
      return;
    }
    state.trackIndex = next;
    state.positionSec = 0;
    loadTrack(tape, state.sideId, next, 0);
    play();
  }

  // Side runs out on its own: audio has already naturally stopped (the
  // browser's own 'ended' event got us here) -- sync state, play the
  // autostop clunk, and show the flip/rewind prompt. No auto-flip: the
  // whole point is the ritual of a visitor choosing to flip the tape.
  function endOfSide() {
    state.playing = false;
    state.positionSec = 0;
    restoreUiSound();
    setTapeMediaSessionPlaybackState('paused'); // 'pause' event listener is skipped for this path (audio.ended is true), so this needs its own explicit update -- the tape's still "there" (flip prompt showing), just not playing, same as a real pause
    saveState();
    playMech(mechAutostopAudio, RADIO_MECH_GAIN);
    showSidePrompt(state.sideId);
  }

  // Flip sequence (used for both A->B "flip" and B->A "rewind" -- same
  // mechanical sound either way per spec). eva_ui_tape_flip.wav already
  // contains its own play-press sound, so the new side starts the
  // instant the flip sound ends, with no separate PLAY clack layered on
  // top of it.
  function flipToSide(sideId) {
    var tape = currentTape();
    if (!tape) { return; }
    hideBubble();
    if (!mechFlipAudio) {
      selectTapeSideAndPlay(tape.id, sideId);
      return;
    }
    var started = false;
    function startSide() {
      if (started) { return; } // 'ended' and a failed play() promise could both fire
      started = true;
      mechFlipAudio.removeEventListener('ended', startSide);
      selectTapeSideAndPlay(tape.id, sideId);
    }
    mechFlipAudio.pause();
    mechFlipAudio.currentTime = 0;
    mechFlipAudio.volume = RADIO_MECH_GAIN;
    mechFlipAudio.addEventListener('ended', startSide);
    var p = mechFlipAudio.play();
    if (p && typeof p.catch === 'function') {
      p.catch(startSide); // autoplay blocked or failed -- don't strand the ritual on a silent flip sound forever
    }
  }

  audio = null; // assigned in init(), listeners wired there once the element exists

  // --- Hard-load resume ---------------------------------------------------
  // Same pattern as the store bell in sound-engine.js: browsers block
  // audio.play() before a real user gesture, so a "was playing" state
  // from before a hard reload can't just resume immediately -- arm it on
  // the first gesture instead.
  var resumeArmed = false;
  function armHardLoadResume() {
    if (resumeArmed || !state.playing) { return; }
    resumeArmed = true;
    function attempt() {
      document.removeEventListener('pointerdown', attempt, true);
      document.removeEventListener('keydown', attempt, true);
      document.removeEventListener('touchstart', attempt, true);
      // The gesture can fire before the manifest fetch resolves on a fast
      // hard-reload -- wait for it rather than silently dropping the resume
      // (tapesReady is a no-op microtask hop once already resolved, which
      // is the common case).
      tapesReady.then(function () {
        var tape = currentTape();
        if (!tape || !state.sideId) { return; }
        deckLoaded = true; // a real tape WAS playing when the page last saved -- this resume is the one legitimate exception to "state.tapeId alone doesn't mean a tape is in the deck," see deckLoaded's own comment
        loadTrack(tape, state.sideId, state.trackIndex, state.positionSec);
        play();
      });
    }
    document.addEventListener('pointerdown', attempt, true);
    document.addEventListener('keydown', attempt, true);
    document.addEventListener('touchstart', attempt, true);
  }

  // --- Hotspot actions -----------------------------------------------

  // ff/rew are deliberately absent here -- they're driven by dedicated
  // pointerdown/up handlers (see onSeekPointerDown/Up below) so a press
  // can be told apart as a tap vs a hold. Leaving them out of ACTIONS
  // means the native 'click' that still follows their pointerup is a
  // harmless no-op instead of double-firing a seek.
  var ACTIONS = {
    door: function () { requestShowRack(); },
    play: function () { dismissBubble(); playWithSound(); },
    pause: function () { pauseWithSound(); },
    stop: function () { stopWithSound(); }
  };

  function onHotspotClick(e) {
    var el = e.target.closest && e.target.closest('.eva-radio-hotspot');
    if (!el) { return; }
    var action = el.getAttribute('data-eva-radio-action');
    if (ACTIONS[action]) { ACTIONS[action](); }
  }

  // A pointerdown/up pair on a hotspot with no real movement should also
  // count as the click above (native click still fires in that case in
  // every real browser -- this pressed-state toggle is purely visual).
  function onHotspotPointerDown(e) {
    var el = e.target.closest && e.target.closest('.eva-radio-hotspot');
    if (!el) { return; }
    el.classList.add('eva-radio-hotspot--pressed');
  }

  function clearPressedState() {
    var pressed = dragHandle.querySelectorAll('.eva-radio-hotspot--pressed');
    pressed.forEach(function (el) { el.classList.remove('eva-radio-hotspot--pressed'); });
  }

  // --- Init ----------------------------------------------------------

  var savePositionTimer = null;

  ready(function () {
    widget = document.getElementById('EvaRadioWidget');
    // iOS SAFARI ONLY (2026-08-26). Safari fires its own non-standard
    // gesturestart/gesturechange/gestureend for a two-finger pinch, IN
    // ADDITION to pointer events, and will page-zoom off them even where
    // touch-action:none and a pointermove preventDefault are already in
    // place. Chromium has no equivalent, which is why this cannot be
    // verified in the sandbox -- it is the most likely cause of Per's
    // "pinch does nothing" (Safari zooming the page instead of the widget
    // taking the gesture). Registered HERE, inside init, because `widget`
    // is still null at module scope. Scoped to the widget, so ordinary
    // page pinch-zoom everywhere else is untouched.
    if (widget) {
      ['gesturestart', 'gesturechange', 'gestureend'].forEach(function (type) {
        widget.addEventListener(type, function (e) { e.preventDefault(); }, { passive: false });
      });
    }
    dragHandle = document.getElementById('EvaRadioDragHandle');
    stage = document.getElementById('EvaRadioStage');
    bubble = document.getElementById('EvaRadioBubble');
    bubbleText = document.getElementById('EvaRadioBubbleText');
    bubbleAction = document.getElementById('EvaRadioBubbleAction');
    bubbleClose = document.getElementById('EvaRadioBubbleClose');
    tooltipEl = document.getElementById('EvaRadioTooltip');
    tooltipTextEl = document.getElementById('EvaRadioTooltipText');
    tooltipTouchedOnce = new Set();
    audio = document.getElementById('EvaRadioAudio');
    if (window.EvaMute) { window.EvaMute.applyToElement(audio); }

    // Register with the site-wide music arbiter (assets/eva-audio-policy.js).
    // TAPES AND STATIONS ARE ONE SOURCE here: they are two <audio>
    // elements but one thing the visitor thinks of as "the radio", and
    // which of the two is playing is the radio's own internal business,
    // not the policy's. The station element is grabbed straight from the
    // DOM rather than reached for through eva-radio-station.js -- both
    // elements are rendered together by snippets/eva-radio.liquid, so
    // both exist by now, and this keeps the whole registration in one
    // place instead of split across two files that do not import each
    // other. Mechanical clacks, FFW wind and station static are NOT
    // registered: they are sound effects, not music, and are meant to be
    // able to overlap.
    if (window.EvaAudioPolicy) {
      var stationAudioEl = document.getElementById('EvaRadioStationAudio');
      window.EvaAudioPolicy.register('radio', {
        elements: [audio, stationAudioEl],
        pause: function () {
          try { if (audio) { audio.pause(); } } catch (e) {}
          try { if (stationAudioEl) { stationAudioEl.pause(); } } catch (e) {}
          // state.playing is deliberately left alone: the transport UI
          // reads the element's own paused state, and rewriting saved
          // state from here would make "the TV interrupted me" look
          // identical to "I pressed STOP" on the next page load.
        }
      });
    }
    if (mediaSessionSupported) {
      try {
        // Same functions the physical PLAY/PAUSE hotspots call -- a
        // lock-screen button press behaves exactly like pressing the
        // real deck button, mechanical clack included.
        navigator.mediaSession.setActionHandler('play', function () { playWithSound(); });
        navigator.mediaSession.setActionHandler('pause', function () { pauseWithSound(); });
        navigator.mediaSession.setActionHandler('nexttrack', function () { nextTrack(); });
        // No 'previoustrack' handler, deliberately -- the deck has no
        // real "previous track" capability (nextTrack() only ever
        // advances forward within a side; there's no equivalent
        // rewind-to-earlier-track function anywhere in this file).
        // Leaving it unset lets the OS grey out/hide that lock-screen
        // button honestly rather than wiring it to a fabricated
        // behavior that doesn't exist in the deck itself.
      } catch (e) {}
    }
    toggleBtns = Array.prototype.slice.call(document.querySelectorAll('.js-eva-radio-toggle'));
    minimizeBtn = document.getElementById('EvaRadioMinimize');
    resizeHandle = document.getElementById('EvaRadioResizeHandle');
    radioToggleEl = document.querySelector('.eva-radio-widget__radio-toggle');
    tapeRackEl = document.getElementById('EvaRadioTapeRack');
    onAirDots = Array.prototype.slice.call(document.querySelectorAll('.js-eva-radio-onair'));
    angleBtn = document.getElementById('EvaRadioAngle');
    angleTransportHotspots = {
      front: document.getElementById('EvaRadioHotspots'),
      above: document.getElementById('EvaRadioHotspotsAbove')
    };
    img = dragHandle && dragHandle.querySelector('.eva-radio-widget__img');
    keyboardHotspotsSvg = document.getElementById('EvaRadioKeyboardHotspots');

    if (!widget || !dragHandle || !audio) { return; }

    // Corner resize handle is meaningless on touch (nothing to hover/
    // grab-drag precisely) and would just sit in the way of the tuner
    // dial next to it -- hidden via CSS class, mouse/desktop unaffected.
    // See eva-radio.css .eva-radio-widget--touch. Pinch (below) is
    // touch's own resize input instead.
    if (IS_TOUCH_CAPABLE) { widget.classList.add('eva-radio-widget--touch'); }

    // Transport (play/stop/ff/rew/door/drag) binds FIRST and on its own --
    // this is the core function of the widget and must never be taken down
    // by a throw in a newer/less-critical subsystem below it.
    try {
      toggleBtns.forEach(function (btn) {
        btn.addEventListener('click', toggleMinimized);
      });
      updateToggleButtonsUI();

      if (minimizeBtn) { minimizeBtn.addEventListener('click', minimizeWidget); }

      // NOT bound at all on touch (2026-08-26). This is the ONLY code
      // path in the widget where a SINGLE pointer's horizontal movement
      // changes the width -- onResizePointerMove is literally
      // `newWidth = resizeStartWidth + (e.clientX - resizeStartX)`, so
      // dragging left SHRINKS and dragging right GROWS. That is exactly
      // the symptom Per reported ("one finger dragging left resizes
      // instead of moving"), and it is directional for the same reason.
      //
      // Until now the handle was bound unconditionally and only CSS
      // (.eva-radio-widget--touch { display:none } in eva-radio.css)
      // kept a finger from ever reaching it -- a single point of failure
      // dressed up as a guard. Anything that stops that one rule
      // applying (a stale cached copy of eva-radio.css, a throw earlier
      // in init before the class is added on the line above, the class
      // being edited out) silently turns the handle back into a live
      // single-finger resizer. The pinch section's own comment already
      // ASSERTED this was "hidden/unbound whenever IS_TOUCH_CAPABLE";
      // the "hidden" half was true and the "unbound" half was not. Now
      // both are.
      if (resizeHandle && !IS_TOUCH_CAPABLE) {
        resizeHandle.addEventListener('pointerdown', onResizePointerDown);
        resizeHandle.addEventListener('pointermove', onResizePointerMove);
        resizeHandle.addEventListener('pointerup', onResizePointerUp);
        resizeHandle.addEventListener('pointercancel', onResizePointerUp);
      }

      bubbleClose.addEventListener('click', dismissBubble);

      dragHandle.addEventListener('pointerdown', onPointerDown);
      dragHandle.addEventListener('pointermove', onPointerMove);
      dragHandle.addEventListener('pointerup', onPointerUp);
      dragHandle.addEventListener('pointercancel', onPointerUp);

      // Pinch tracking is bound on the widget itself, not dragHandle --
      // see the "--- Pinch-to-zoom ---" section above for why. These are
      // no-ops for every mouse/pen event (pointerType guard), so binding
      // them unconditionally (not just when IS_TOUCH_CAPABLE) is safe and
      // also covers a touch-capable device the feature-detect somehow
      // missed.
      widget.addEventListener('pointerdown', onPinchTrackPointerDown);
      widget.addEventListener('pointermove', onPinchTrackPointerMove, { passive: false });
      widget.addEventListener('pointerup', onPinchTrackPointerUp);
      widget.addEventListener('pointercancel', onPinchTrackPointerUp);

      dragHandle.addEventListener('pointerdown', onHotspotPointerDown);
      dragHandle.addEventListener('pointerup', clearPressedState);
      dragHandle.addEventListener('pointercancel', clearPressedState);
      dragHandle.addEventListener('click', onHotspotClick);

      // Every angle has its own hotspot SVG (only one visible/interactive
      // at a time via display:none, see applyAngle()), each with its own
      // ff/rew polygon -- bind all of them, not just the front one, or
      // FF/REW would silently do nothing on the other 3 angles.
      var ffHotspots = dragHandle.querySelectorAll('[data-eva-radio-action="ff"]');
      var rewHotspots = dragHandle.querySelectorAll('[data-eva-radio-action="rew"]');
      ffHotspots.forEach(function (el) {
        el.addEventListener('pointerdown', onSeekPointerDown('ff'));
        el.addEventListener('pointerup', onSeekPointerUp('ff'));
        el.addEventListener('pointercancel', onSeekPointerCancel);
      });
      rewHotspots.forEach(function (el) {
        el.addEventListener('pointerdown', onSeekPointerDown('rew'));
        el.addEventListener('pointerup', onSeekPointerUp('rew'));
        el.addEventListener('pointercancel', onSeekPointerCancel);
      });

      // Tooltip chip: one delegated pair covers every hotspot with a
      // data-eva-tooltip attribute (deck buttons, door, tone toggle,
      // rhythm presets across all 4 angles) rather than per-control
      // wiring. pointerover/out (filtered to real mice) rather than
      // mouseover/out -- touch devices fire synthetic mouse events right
      // after a real touch, and an unfiltered mouseout was hiding the
      // touch-triggered tooltip within milliseconds instead of leaving it
      // up for its full 1.5s.
      dragHandle.addEventListener('pointerover', function (e) {
        if (e.pointerType !== 'mouse') { return; }
        var el = e.target.closest && e.target.closest('[data-eva-tooltip]');
        if (el) { showTooltip(el, el.getAttribute('data-eva-tooltip')); }
      });
      dragHandle.addEventListener('pointerout', function (e) {
        if (e.pointerType !== 'mouse') { return; }
        var el = e.target.closest && e.target.closest('[data-eva-tooltip]');
        if (el && !el.contains(e.relatedTarget)) { hideTooltip(); }
      });
      // Touch has no hover -- show the chip briefly on a control's FIRST
      // touch this page load only, per Per's spec, rather than on every tap.
      dragHandle.addEventListener('touchstart', function (e) {
        var el = e.target.closest && e.target.closest('[data-eva-tooltip]');
        if (!el || tooltipTouchedOnce.has(el)) { return; }
        tooltipTouchedOnce.add(el);
        showTooltip(el, el.getAttribute('data-eva-tooltip'));
        setTimeout(hideTooltip, 1500);
      }, { passive: true });

      audio.addEventListener('play', function () {
        state.playing = true;
        // The element is genuinely playing -- this is one of exactly two
        // "music has started" signals on the site (see
        // eva-audio-policy.js). Bound to the native event rather than to
        // play()/playWithSound() on purpose: those are intent, this is
        // fact, and a play() that the browser blocks must not unlock the
        // UI blip layer.
        if (window.EvaAudioPolicy) { window.EvaAudioPolicy.noteMusicStarted(); }
        duckUiSound();
        updateTapeMediaSession();
        saveState();
      });
      audio.addEventListener('pause', function () {
        // 'pause' also fires right before 'ended' -- ended's own handler
        // (nextTrack/endOfSide) is authoritative for that case, this just
        // covers an explicit user pause/stop.
        if (audio.ended) { return; }
        state.playing = false;
        restoreUiSound();
        setTapeMediaSessionPlaybackState('paused');
      });
      audio.addEventListener('ended', function () {
        nextTrack();
      });
      audio.addEventListener('timeupdate', function () {
        if (!state.playing) { return; }
        if (savePositionTimer) { return; }
        savePositionTimer = setTimeout(function () {
          savePositionTimer = null;
          state.positionSec = audio.currentTime || 0;
          saveState();
        }, SAVE_INTERVAL_MS);
      });

      // Small public hook for eva-radio-tape.js (tape browser) -- the one
      // and only path that ever starts playback, driven by a real
      // drag/tap-insert in the rack.
      window.EvaRadioWidget = {
        playTape: function (tapeId, sideId) {
          selectTapeSide(tapeId, sideId || 'a');
        },
        // [] until tapesReady resolves -- eva-radio-tape.js checks this
        // synchronously first (covers a fetch that already resolved by the
        // time its own ready() runs), falling back to the
        // 'eva:radio:tapes:ready' event for the other ordering.
        getTapes: function () { return tapes; },
        // Reused by eva-radio-tape.js for the tape object's own hover/
        // touch tooltip -- one chip, one positioning implementation.
        showTooltip: showTooltip,
        hideTooltip: hideTooltip,
        // Reused by eva-radio-tape.js when the rack scroller switches
        // which tape is centered -- whatever was playing shouldn't keep
        // going once the visible tape object no longer matches it.
        stop: stop,
        // Side-effect-free read (2026-08-07) -- lets eva-radio-station.js
        // check "is a tape genuinely playing right now" before calling
        // stop(), rather than calling it unconditionally on every SCAN
        // attempt/retry. stop() always dispatches eva:radio:playstate
        // with reason:'stop', which eva-radio-tape.js's own listener
        // turns into a visible cassette-door pop flash -- harmless to
        // call when nothing's playing in terms of AUDIO state, but very
        // much not harmless visually: it was firing that flash on every
        // single SCAN retry, animating the TAPE DECK for something
        // happening in the tuner instead. See attemptPlay()'s own gate.
        isPlaying: function () { return !!state.playing; }
      };
    } catch (transportErr) {
      console.error('[eva-radio] transport init failed', transportErr);
    }

    // Angle changer + keyboard -- separate subsystem, own try/catch so
    // a failure here can never prevent transport (above) from working.
    try {
      initMechSounds();
      preloadAngleImages();
      applyAngle(); // restores the persisted angle photo + hotspot visibility

      // Keys duck the tape a tad instead of pausing it -- both play at
      // once now (eva-radio-keyboard.js dispatches this on every active/
      // idle transition, not per keypress).
      document.addEventListener('eva:radio:keyboard:active', function (e) {
        keyboardDucking = !!(e.detail && e.detail.active);
        updateAudioVolume();
      });

      if (angleBtn) { angleBtn.addEventListener('click', cycleAngle); }
    } catch (modeErr) {
      console.error('[eva-radio] angle/mode init failed', modeErr);
    }

    // Widget chrome: initial show/position + PJAX/lifecycle hooks.
    try {
      if (!state.minimized) {
        widget.hidden = false;
        positionWidget();
      }
      updateOnAirDot();

      document.addEventListener('eva:pjax:loaded', function () {
        if (!widget || widget.hidden) { return; }
        var pos = clampPosition(state.x, state.y);
        state.x = pos.x;
        state.y = pos.y;
        applyPosition(pos.x, pos.y);
        saveState();
      });

      armHardLoadResume();

      window.addEventListener('beforeunload', function () {
        if (state.playing) {
          state.positionSec = audio.currentTime || 0;
        }
        saveState();
      });
    } catch (chromeErr) {
      console.error('[eva-radio] widget chrome init failed', chromeErr);
    }
  });
})();
